import type { ApplicationVersion } from '@/micro/types/version.type'
import { IcpProduct, type IcpProductValue } from '@/micro/types/register.type'
import { getProductFromQuery } from '@/micro/utils/icpRegister'
import { getPOSPathFromURL } from '@/micro/utils/urls'

/** Países con flujo de registro ICP. Agregar aquí al habilitar ICP en otra versión. */
export const ICP_REGISTER_COUNTRIES: readonly ApplicationVersion[] = [
  'costaRica',
  'colombia',
  'mexico',
  'republicaDominicana',
]

const ICP_PRODUCT_LOGIN_PATH: Partial<Record<IcpProductValue, string>> = {
  [IcpProduct.POS]: 'pos',
  [IcpProduct.NOMINA]: 'nomina',
}

export type IcpRegisterNavigationTarget = {
  path: string
  query?: Record<string, string>
}

export const isIcpRegisterCountry = (country: ApplicationVersion): boolean =>
  ICP_REGISTER_COUNTRIES.includes(country)

export const getIcpCountryFromRegisterRoute = (location?: string): ApplicationVersion | null => {
  const href = location ?? (typeof window !== 'undefined' ? window.location.href : '')
  if (!href) return null

  let pathname: string
  try {
    pathname = new URL(href).pathname
  } catch {
    return null
  }

  return (
    ICP_REGISTER_COUNTRIES.find(country =>
      new RegExp(`/user/register/country/${country}`, 'i').test(pathname),
    ) ?? null
  )
}

/** Extrae el país ICP desde un path de Vue Router (sin protocolo/host). */
export const getIcpCountryFromPath = (path: string): ApplicationVersion | null => {
  if (!path) return null

  return (
    ICP_REGISTER_COUNTRIES.find(country =>
      new RegExp(`/user/register/country/${country}`, 'i').test(path),
    ) ?? null
  )
}

/** Registro ICP con ?product=POS → login dedicado (/user/login/pos). */
export const resolveIcpLoginPathFromRegister = (location?: string): string | null => {
  if (!getIcpCountryFromRegisterRoute(location)) return null

  const product = getProductFromQuery(location)
  if (!product) return null

  const loginSegment = ICP_PRODUCT_LOGIN_PATH[product]
  if (!loginSegment) return null

  return `user/login/${loginSegment}`
}

export const buildIcpRegisterRouteForCountry = (
  country: ApplicationVersion,
  product?: IcpProductValue | null,
): IcpRegisterNavigationTarget | null => {
  if (!isIcpRegisterCountry(country)) return null

  return {
    path: `user/register/country/${country}`,
    ...(product ? { query: { product } } : undefined),
  }
}

/** Login POS + país ICP → registro ICP con ?product=POS. */
export const resolveIcpRegisterPathFromLogin = (
  country: ApplicationVersion,
): IcpRegisterNavigationTarget | null => {
  if (getPOSPathFromURL() !== 'pos') return null

  return buildIcpRegisterRouteForCountry(country, IcpProduct.POS)
}

export const buildIcpRegisterNavigationUrl = (
  target: IcpRegisterNavigationTarget,
  baseUrl: string,
): string => {
  const query = target.query ? `?${new URLSearchParams(target.query).toString()}` : ''
  return `${baseUrl}${target.path}${query}`
}
